import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { CustomInput, UseFormDropDown } from '../../../components/common';
import AlertBox from '../../../components/route-leaving-prompt/alert-box';
import { useChunkedUpload } from '../../../hooks/useChunkedUpload';
import Modal from '../../../components/common/modal';
import { toast } from 'react-toastify';

interface ExperienceFormPayload {
  title: string;
  video_id: string;
  message_quality: string;
}

interface CreateExperienceModalProps {
  listenerId: number;
  experienceType: 'audio' | 'video';
  visible: boolean;
  onClose: () => void;
}

export const CreateExperienceModal: React.FunctionComponent<CreateExperienceModalProps> = ({
  listenerId,
  experienceType,
  visible,
  onClose,
}) => {
  const isVideo = experienceType === 'video';
  const queryClient = useQueryClient();
  const { upload: chunkedUpload, progress: uploadProgress, cancel: cancelUpload, isUploading } = useChunkedUpload();
  const [alertModal, setAlertModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultValues: ExperienceFormPayload = {
    title: '',
    video_id: '',
    message_quality: '7',
  };

  const {
    formState: { isDirty, errors },
    handleSubmit,
    register,
    reset,
  } = useForm({ defaultValues });

  const isDirtyOrFile = isDirty || !!selectedFile;
  const hasErrors = Object.keys(errors).length > 0;

  const handleClose = () => {
    if (isDirtyOrFile) {
      setAlertModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    reset();
    setSelectedFile(null);
    onClose();
    setAlertModal(false);
  };

  const onSubmit = async (formData: ExperienceFormPayload) => {
    if (!selectedFile) {
      toast.error(`Please select ${isVideo ? 'a video' : 'an audio'} file to upload`);
      return;
    }

    setIsSubmitting(true);
    try {
      await chunkedUpload(selectedFile, {
        listener_role_id: listenerId,
        title: formData.title || undefined,
        upload_source: isVideo ? 'SB_VIDEO_UPLOAD' : 'SB_AUDIO_UPLOAD',
        message_quality: Number(formData.message_quality),
        video_id: isVideo ? formData.video_id || undefined : undefined,
      });
      toast.success('Experience Created Successfully');
      queryClient.invalidateQueries(['listener_audio']);
      reset();
      setSelectedFile(null);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isModel={visible}
        InnerComponent={
          <div
            className="inline-block transform overflow-hidden border bg-white align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit(onSubmit)} id="create-experience-form">
              <div className="bg-white px-2 pb-2 pt-3 sm:p-6 sm:pb-4">
                <h3 className="blue-primary py-1 text-2xl font-normal">
                  Create {isVideo ? 'Video' : 'Audio'} Experience
                </h3>

                <div className="mt-4">
                  <label className="text-gray-dark mb-1 block text-sm font-medium">
                    {isVideo ? 'Video File' : 'Audio File'} <span className="text-red">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Choose File
                    </button>
                    <span className="text-sm text-gray-600">
                      {selectedFile ? selectedFile.name : 'No file selected'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={isVideo ? 'video/*' : 'audio/*'}
                      className="hidden"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                </div>

                <div className="flex-start mt-4 flex py-2">
                  <h3 className="text-gray-dark py-1 text-lg font-bold">Details</h3>
                </div>

                <div className="mr-5 grid grid-cols-2 gap-3 gap-y-5">
                  {isVideo && (
                    <div>
                      <CustomInput
                        dataTestId="vimeo_id"
                        inputType="text"
                        Label="Vimeo ID"
                        register={{
                          ...register('video_id', {
                            validate: (value) => {
                              if (!value) return true;
                              if (!/^\d+$/.test(value)) return 'Vimeo ID must contain numbers only';
                              if (value.length !== 10) return 'Vimeo ID must be exactly 10 digits';
                              return true;
                            },
                          }),
                        }}
                        Error={!!errors.video_id}
                        ErrorMessage={errors.video_id?.message ?? ''}
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-gray-dark mb-1 block text-sm font-medium">Title</label>
                    <input
                      type="text"
                      className="text-gray-dark bg-gray-background-light w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
                      {...register('title')}
                    />
                  </div>

                  <div className="my-2 text-left">
                    <UseFormDropDown label="Grade">
                      <select
                        className="select-box w-full focus:ring-transparent"
                        defaultValue="7"
                        {...register('message_quality')}
                      >
                        <option value="7">Ungraded</option>
                        <option value="3">Approved</option>
                        <option value="10">Rejected</option>
                      </select>
                    </UseFormDropDown>
                  </div>
                </div>

                {isUploading && (
                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between text-sm text-gray-600">
                      <span>Uploading… {uploadProgress.percent}%</span>
                      <button type="button" className="text-xs text-red-500 hover:underline" onClick={cancelUpload}>
                        Cancel Upload
                      </button>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                      <div
                        className="bg-blue-primary h-full transition-all"
                        style={{ width: `${uploadProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-2 flex justify-end gap-4">
                  <button
                    className={
                      'h-8 w-16 rounded-sm px-2 text-center text-sm text-white ' +
                      (isDirtyOrFile && !isSubmitting && !hasErrors ? 'bg-blue-primary' : 'bg-gray-200')
                    }
                    type="submit"
                    form="create-experience-form"
                    disabled={!isDirtyOrFile || isSubmitting || hasErrors}
                  >
                    {isSubmitting ? '...' : 'Save'}
                  </button>
                  <button
                    className="bg-blue-primary h-8 w-16 rounded-sm px-2 text-center text-sm text-white"
                    type="button"
                    onClick={handleClose}
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        }
      />

      <AlertBox
        visible={alertModal}
        handlePrompt={handleDiscard}
        closeModal={() => setAlertModal(false)}
        confirmButtonText="Discard Changes"
        titleText="Alert"
        contentText="You have unsaved changes. If you leave this screen without saving, your changes will be lost."
        cancelButtonText="Cancel"
      />
    </>
  );
};
